import { Loader2, Plus, Star, Trash2, Truck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SupplierPicker } from '@/components/ui/supplier-picker';
import {
  useCreatePartSupplier,
  useDeletePartSupplier,
  usePartSuppliers,
  useUpdatePartSupplier,
} from '@/features/part-suppliers/queries';
import type { PartSupplierLink } from '@/features/part-suppliers/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface PartSuppliersCardProps {
  partId: string;
}

function formatPrice(cents: number | null, currency: string): string {
  if (cents === null) return '—';
  const amount = cents / 100;
  // Intl.NumberFormat handles the symbol + locale formatting; we
  // pin to the user's browser locale via undefined.
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function PartSuppliersCard({ partId }: PartSuppliersCardProps) {
  const { t } = useTranslation();
  const { data: rows, isLoading } = usePartSuppliers(partId);
  const createMut = useCreatePartSupplier(partId);
  const updateMut = useUpdatePartSupplier(partId);
  const deleteMut = useDeletePartSupplier(partId);

  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PartSupplierLink | null>(
    null,
  );
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Add-supplier form state — kept local rather than a full RHF
  // setup since the form has 5 trivial fields.
  const [pickedIds, setPickedIds] = useState<string[]>([]);
  const [supplierPartNumber, setSupplierPartNumber] = useState('');
  const [unitPriceCents, setUnitPriceCents] = useState<string>('');
  const [currency, setCurrency] = useState('EUR');
  const [leadTimeDays, setLeadTimeDays] = useState<string>('');
  const [isPreferred, setIsPreferred] = useState(false);

  const resetAddForm = () => {
    setPickedIds([]);
    setSupplierPartNumber('');
    setUnitPriceCents('');
    setCurrency('EUR');
    setLeadTimeDays('');
    setIsPreferred(false);
  };

  const handleAdd = () => {
    if (pickedIds.length === 0) return;
    setErrorKey(null);
    createMut.mutate(
      {
        supplierId: pickedIds[0],
        supplierPartNumber: supplierPartNumber.trim() || undefined,
        unitPriceCents: unitPriceCents
          ? Math.round(parseFloat(unitPriceCents) * 100)
          : undefined,
        currency: currency.toUpperCase() || undefined,
        leadTimeDays: leadTimeDays ? parseInt(leadTimeDays, 10) : undefined,
        isPreferred,
      },
      {
        onSuccess: () => {
          setAddOpen(false);
          resetAddForm();
        },
        onError: (err) => {
          if (err instanceof ApiError) setErrorKey(err.key);
          else setErrorKey('part.suppliers.saveError');
        },
      },
    );
  };

  const handleTogglePreferred = (link: PartSupplierLink) => {
    updateMut.mutate({
      partSupplierId: link.id,
      patch: { isPreferred: !link.isPreferred },
    });
  };

  const handleDelete = (link: PartSupplierLink) => {
    deleteMut.mutate(link.id, {
      onSuccess: () => setPendingDelete(null),
    });
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <Truck aria-hidden className="h-4 w-4" />
            {t('part.suppliers.title')}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
            <Plus aria-hidden />
            {t('part.suppliers.add')}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (rows ?? []).length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t('part.suppliers.empty')}
            </div>
          ) : (
            <ul className="space-y-2">
              {(rows ?? []).map((link) => (
                <li
                  key={link.id}
                  className="flex flex-wrap items-center gap-3 rounded-md border p-3 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{link.supplier.name}</span>
                      {link.supplier.countryCode && (
                        <span className="font-mono text-xs uppercase text-muted-foreground">
                          {link.supplier.countryCode}
                        </span>
                      )}
                      {link.isPreferred && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                          <Star
                            aria-hidden
                            className="h-3 w-3 fill-current"
                          />
                          {t('part.suppliers.preferred')}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {link.supplierPartNumber && (
                        <span>
                          {t('part.suppliers.partNumberLabel')}:{' '}
                          <span className="font-mono">
                            {link.supplierPartNumber}
                          </span>
                        </span>
                      )}
                      <span>
                        {t('part.suppliers.priceLabel')}:{' '}
                        {formatPrice(link.unitPriceCents, link.currency)}
                      </span>
                      {link.leadTimeDays !== null && (
                        <span>
                          {t('part.suppliers.leadTimeLabel')}:{' '}
                          {t('part.suppliers.leadTimeDays', {
                            count: link.leadTimeDays,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={link.isPreferred ? 'default' : 'outline'}
                      onClick={() => handleTogglePreferred(link)}
                      disabled={updateMut.isPending}
                      title={t('part.suppliers.togglePreferred')}
                    >
                      <Star
                        aria-hidden
                        className={cn(
                          'h-4 w-4',
                          link.isPreferred && 'fill-current',
                        )}
                      />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setPendingDelete(link)}
                      disabled={deleteMut.isPending}
                    >
                      <Trash2 aria-hidden className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Add-supplier modal */}
      <Dialog
        open={addOpen}
        onOpenChange={(o) => {
          if (!o) {
            setAddOpen(false);
            resetAddForm();
            setErrorKey(null);
            createMut.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('part.suppliers.addTitle')}</DialogTitle>
            <DialogDescription>
              {t('part.suppliers.addDescription')}
            </DialogDescription>
          </DialogHeader>

          {errorKey && (
            <Alert variant="destructive">
              <AlertTitle>{t('part.suppliers.saveError')}</AlertTitle>
              <AlertDescription>
                {t(errorKey, { defaultValue: t('part.suppliers.saveError') })}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t('part.suppliers.supplierLabel')}
              </label>
              <SupplierPicker
                value={pickedIds}
                onChange={(next) => {
                  // Single-select-ish — keep only the most recent
                  // pick so we never pass more than one supplierId.
                  setPickedIds(next.slice(-1));
                }}
                placeholder={t('part.suppliers.supplierPlaceholder')}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  {t('part.suppliers.partNumberLabel')}
                </label>
                <Input
                  value={supplierPartNumber}
                  onChange={(e) => setSupplierPartNumber(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  {t('part.suppliers.priceLabel')}
                </label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={unitPriceCents}
                    onChange={(e) => setUnitPriceCents(e.target.value)}
                  />
                  <Input
                    className="w-20"
                    maxLength={3}
                    value={currency}
                    onChange={(e) =>
                      setCurrency(e.target.value.toUpperCase().slice(0, 3))
                    }
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  {t('part.suppliers.leadTimeLabel')}
                </label>
                <Input
                  type="number"
                  min="0"
                  placeholder="14"
                  value={leadTimeDays}
                  onChange={(e) => setLeadTimeDays(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 self-end pb-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer accent-primary"
                  checked={isPreferred}
                  onChange={(e) => setIsPreferred(e.target.checked)}
                />
                {t('part.suppliers.preferredLabel')}
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setAddOpen(false)}
              disabled={createMut.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAdd}
              disabled={pickedIds.length === 0 || createMut.isPending}
            >
              {createMut.isPending && (
                <Loader2 className="animate-spin" aria-hidden />
              )}
              {t('part.suppliers.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title={t('part.suppliers.confirmRemoveTitle')}
        description={t('part.suppliers.confirmRemoveDescription', {
          name: pendingDelete?.supplier.name ?? '',
        })}
        confirmLabel={t('common.delete')}
        variant="destructive"
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete);
        }}
      />
    </>
  );
}
